import { confirmable } from 'react-confirm';
import StackedModal from './stacked_modal';
import { useTranslation } from 'react-i18next';
import { Button, DropZone, Icon, IndexTable } from '@shopify/polaris';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { defaultCustomerParam, useGetMe, useGetUsersWithParams } from 'src/api/user_api';
import { RHFAPIAutocompleteV2 } from '../hook_form/rhf_api_autocomplete_v2';
import { getUserName } from 'src/utils/format_data';
import FormProvider from '../hook_form/form_provider';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { isEmpty } from 'src/utils/type_check';
import FixedTable from '../table/fixed_table';
import useTable from 'src/hooks/use_table';
import { AlertTriangleIcon } from '@shopify/polaris-icons';
import { getExcelTemplate } from 'src/api/excel_api';
import { LogInAlert } from '../alert/alert';

function ImportDialog({ show, proceed, type, importData, dataKey }) {
  const { t } = useTranslation(['excel']);
  const [file, setFile] = useState(null);
  const { customerId, isAdmin } = useGetMe();
  const [warningMessage, setWarningMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorData, setErrorData] = useState([]);
  const [page, setPage] = useState(1);

  const schema = Yup.object().shape({
    customerId: Yup.string(),
  });

  const defaultValues = {
    customerId: customerId,
  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    setWarningMessage('');
    try {
      if (data === null) return;
      var res = await importData(data.customerId, file);
      if (res.isSuccess) {
        var temp = res.result
          .filter((x) => !x.isCreated)
          .map((r) => {
            return { ...r, id: r[dataKey], errorMessage: r.errorMessages[0] };
          });
        setErrorData(temp);
        var total = res.result.length;
        var fail = temp.length;
        var success = total - fail;
        if (temp.length > 0) {
          setWarningMessage(
            `${t('excel:import_error_success', {
              total: total,
              success: success,
              count: success,
            })}${t('excel:import_error_fail', {
              fail: fail,
              count: fail,
            })}`,
          );
        } else {
          setSuccessMessage(t('excel:import_success', { total: total }));
        }
      } else {
        throw Error();
      }
    } catch (error) {
      setWarningMessage(t('excel:generic_error'));
    }
  });

  const handleDropZoneDrop = async (_dropFiles, acceptedFiles, _rejectedFiles) => {
    setFile(acceptedFiles[0]);
  };

  const renderCustomer = (
    <Box>
      <Typography variant={'heading_sm'}>{t('common:field.customer')}</Typography>
      <RHFAPIAutocompleteV2
        name="customerId"
        resetSearchOnOpen
        useOptions={useGetUsersWithParams}
        params={{ ...defaultCustomerParam, pageSize: 100 }}
        toOption={(r) => ({
          label: getUserName(r),
          value: r.userId,
        })}
        searchParamName={'userName'}
      />
    </Box>
  );

  const dropFile =
    file === null ? (
      <DropZone.FileUpload
        actionTitle={t('excel:add')}
        actionHint={`${t('excel:accepts')} .xlsx`}
      />
    ) : (
      <Stack
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        direction={'row'}
        px={2}
        py={4}
      >
        <Typography variant="body_md_semibold">{file?.name}</Typography>
        <Button onClick={() => setFile(null)}>{t('excel:replace')}</Button>
      </Stack>
    );

  const renderAlert = (
    <>
      {!isEmpty(warningMessage) && (
        <LogInAlert
          onClose={() => setWarningMessage('')}
          severity="error"
          sx={{ py: 0 }}
          errorMsg={warningMessage}
        />
      )}
      {!isEmpty(successMessage) && (
        <Alert
          onClose={() => setSuccessMessage('')}
          sx={{ py: 0 }}
        >
          {successMessage}
        </Alert>
      )}
    </>
  );

  const table = useTable({
    data: errorData.slice((page - 1) * 10, 10 * page),
    headers: [{ title: '' }, { title: t(`excel:${dataKey}`) }, { title: t('excel:error_cause') }],
    totalDataCount: errorData.length,
    showPagination: errorData.length > 10,
    showRowsPerPage: false,
    rowsPerPageList: [10],
    rowMarkup: ({ data }) => {
      return data.map(({ id, errorMessage }, index) => (
        <IndexTable.Row
          id={index}
          key={index}
          position={index}
          onClick={() => {}}
        >
          <Box
            component={IndexTable.Cell}
            sx={{ width: '40px' }}
          >
            <Icon
              source={AlertTriangleIcon}
              tone="critical"
            />
          </Box>
          <Box
            component={IndexTable.Cell}
            sx={{ width: '45%' }}
          >
            {id}
          </Box>
          <Box
            component={IndexTable.Cell}
            sx={{ width: '45%' }}
          >
            {errorMessage}
          </Box>
        </IndexTable.Row>
      ));
    },
  });

  if (table.isRefetch.value) {
    setPage(table.page);
    table.onRefetched();
  }

  const renderErrorTable = <FixedTable useTable={table} />;

  const content = (
    <FormProvider
      methods={methods}
      onSubmit={onSubmit}
    >
      <Stack spacing={1.5}>
        {renderAlert}
        {isAdmin && renderCustomer}
        <DropZone
          onDrop={handleDropZoneDrop}
          allowMultiple={false}
          variableHeight
          accept=".xlsx"
        >
          {dropFile}
        </DropZone>
        {warningMessage && errorData.length ? renderErrorTable : null}
      </Stack>
    </FormProvider>
  );
  return (
    <StackedModal
      isOpen={show}
      title={t('common:button.import')}
      content={content}
      primaryAction={{
        content: t('common:button.import'),
        onAction: onSubmit,
        loading: isSubmitting,
        disabled: (isAdmin && !isDirty) || file === null,
        isSubmit: true,
      }}
      footerLeftNode={
        <Button
          variant="tertiary"
          onClick={async () => {
            await getExcelTemplate(type);
          }}
        >
          <Typography
            variant={'body_md'}
            sx={{ color: 'link', textDecoration: 'underline' }}
          >
            {t('excel:download')}
          </Typography>
        </Button>
      }
      onClose={() => {
        proceed(null);
      }}
    />
  );
}

export default confirmable(ImportDialog);
